import { del, get, post, put } from '@/lib/api/client'
import {
  PublicationFilterParamsSchema,
  PaginationParamsSchema,
  type Publication,
  type PublicationCategory,
  type PublicationFilterParams,
  type PublicationStats,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams,
  CreatePublication,
  CreatePublicationSchema,
  UpdatePublication,
  UpdatePublicationSchema
} from '@/lib/api/types'

export class PublicationService {

  async getPublications(
    params: PaginationParams = { page: 1, size: 10 },
    filters: PublicationFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Publication>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = PublicationFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<Publication>>>(
      `publications/private/filter?${query}`,
      validatedFilters
    )
  }


  async getPublicationStats(): Promise<ApiResponse<PublicationStats>> {
    const response = await get<ApiResponse<PublicationStats>>('publications/private/stats')
    return response
  }


  async getPublicationById(publicationId: string): Promise<ApiResponse<Publication>> {
    return await get<ApiResponse<Publication>>(
      `publications/private/${publicationId}`
    )
  }

  async createPublication(data: CreatePublication): Promise<ApiResponse<Publication>> {

    const validationData = CreatePublicationSchema.parse(data)

    return await post<ApiResponse<Publication>>(
      'publications/admin/private',
      validationData
    )
  };

  async updatePublication(publicationId: string, data: UpdatePublication): Promise<ApiResponse<Publication>> {

    const validationData = UpdatePublicationSchema.parse(data)

    return await put<ApiResponse<Publication>>(
      `publications/admin/private/${publicationId}`,
      validationData
    )
  }


  async updatePublicationStatus(publicationId: string, status: 'DRAFT' | 'PUBLISH'): Promise<ApiResponse<Publication>> {
    return await put<ApiResponse<Publication>>(
      `publications/admin/private/${publicationId}/status`,
      { status }
    )
  }

  async deletePublication(publicationId: string): Promise<ApiResponse<null>> {
    return await del<ApiResponse<null>>(
      `publications/admin/private/${publicationId}`
    )
  }

  async getPublicationCategories(): Promise<ApiResponse<PublicationCategory[]>> {
    return await get<ApiResponse<PublicationCategory[]>>(
      'publications/private/categories'
    )
  }

  async searchPublications(
    params: PaginationParams = { page: 1, size: 10 },
    searchBy?: string
  ): Promise<ApiResponse<PaginatedResponse<Publication>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const queryParams = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    })

    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<PaginatedResponse<Publication>>>(
      `publications/private/search?${queryParams.toString()}`
    )
  }

  /**
   * Get Key Resources highlights
   * GET /publications/public/highlights?type=HOME_PAGE_KEY_RESOURCES
   */
  async getPublicationHighlights(type: string = 'HOME_PAGE_KEY_RESOURCES'): Promise<ApiResponse<Publication[]>> {
    return await get<ApiResponse<Publication[]>>(
      `publications/public/highlights?type=${type}`
    )
  }

  /**
   * Update Key Resources highlights
   * POST /publications/private/highlight
   */
  async updatePublicationHighlights(
    publicationIds: string[],
    type: string = 'HOME_PAGE_KEY_RESOURCES'
  ): Promise<ApiResponse<null>> {
    return await post<ApiResponse<null>>('publications/private/highlight', {
      publicationIds,
      highlightType: type
    })
  }
}


export const publicationService = new PublicationService()

// Export default
export default publicationService
